import useRealtimeDraft from '../../realtime/useRealtimeDraft';
import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Form, Spinner, Alert, Button } from 'react-bootstrap';
import { panelRequest, findPanelRecord, serializePanelSave, withoutEditedField } from './panelSizingRequests';
import { selectApiUrls, selectUpdatedApis } from '../../redux/slices/apiUrlSlice';
import { selectUserDetails, selectUserRoles } from '../../redux/slices/userSlice';
import { getProjectId, idsMatch } from '../../utils/dataNormalization';

/**
 * PanelSizingLog Component - Displays electrical job data from multiple tables
 * Visible to Admin users only
 * @param {Array} generalProjectData - General project data from parent
 * @param {Object} electricalOptionsData - Electrical options data mapped by ProjectID
 * @param {Object} waterTreatmentData - Water treatment data mapped by ProjectID
 * @param {Object} generalStationData - General station data mapped by ProjectID
 * @param {Object} fertigationData - Fertigation data mapped by ProjectID
 * @param {Object} pumpsData - Pumps data mapped by ProjectID
 * @param {Object} panelSizingData - Panel sizing data mapped by ProjectID
 * @param {boolean} loading - Loading state from parent
 * @param {string} error - Error message from parent
 * @param {Function} showSaveNotificationMessage - Callback to show save notifications
 * @param {Function} onDataUpdate - Callback to refresh data after updates
 */
function PanelSizingLog({ 
  generalProjectData = [], 
  electricalOptionsData = {}, 
  waterTreatmentData = {}, 
  generalStationData = {}, 
  fertigationData = {}, 
  pumpsData = {},
  panelSizingData = {},
  loading = false,
  error = null,
  showSaveNotificationMessage = null,
  onDataUpdate = null
}) {
  
  // Redux selectors
  const dispatch = useDispatch();
  const apiUrls = useSelector(selectApiUrls);
  const updatedApis = useSelector(selectUpdatedApis);
  const userDetails = useSelector(selectUserDetails);
  const userRoles = useSelector(selectUserRoles);
  const { options } = useSelector((state) => state.dropdown);
  
  // Get current user info - match pattern from useActivityLogChangeLog
  const currentUser = userDetails?.name || userDetails?.displayName || "Unknown";
  const primaryRole = userRoles?.length > 0 ? userRoles[0] : "User";
  
  // Filter and search states
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'JobNumber', direction: 'ascending' });
  const [copiedJobId, setCopiedJobId] = useState(null);
  const [copiedFolderJobId, setCopiedFolderJobId] = useState(null);
  
  // Edit state - track changes by projectId and field
  const [editedValues, setEditedValues] = useState({});
  const [savingFields, setSavingFields] = useState({});
  const [editingFields, setEditingFields] = useState({});
  useRealtimeDraft(Object.values(editingFields).some(Boolean) || Object.values(savingFields).some(Boolean) || Object.keys(editedValues).length > 0); // Track which fields are in edit mode
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50); // Show 50 items per page

  // Combine data from all tables into a single row object
  const combinedData = useMemo(() => {
    return generalProjectData
      .map(project => {
        const projectId = project.ID;
        const elecOptions = electricalOptionsData[projectId] || {};
        const waterTreatment = waterTreatmentData[projectId] || {};
        const generalStation = generalStationData[projectId] || {};
        const fertigation = fertigationData[projectId] || {};
        const pumps = pumpsData[projectId] || {};
        const panelSizing = panelSizingData[projectId] || {};
        
        return {
          // General Project fields
          ID: project.ID,
          ElectricalDone: project.ElectricalDone,
          ElectricalDue: project.ElectricalDue,
          JobNumber: project.JobNumber,
          ProjectName: project.ProjectName,
          ModelNumber: project.ModelNumber,
          Zones: project.Zones,
        
        // Electrical Options fields
        ElectricalEnclosureActualSize: elecOptions.ElectricalEnclosureActualSize,
        Sunshade: elecOptions.Sunshade,
        DeadFront: elecOptions.DeadFront,
        ElectricalOptions: elecOptions.ElectricalOptions,
        IndividualMotorDisconnects: elecOptions.IndividualMotorDisconnects,
        
        // Water Treatment fields
        HasUV: waterTreatment.HasUV,
        
        // General Station fields
        PrimeSystem: generalStation.PrimeSystem,
        
        // Fertigation fields
        WatertronicsFertigation: fertigation.WatertronicsFertigation,
        
        // Pumps fields
        HasTransfer: pumps.HasTransfer,
        HasWaterFeature: pumps.HasWaterFeature,
        
        // Panel Sizing fields
        Orientation: panelSizing.Orientation,
        Drives: panelSizing.Drives,
        Cooling: panelSizing.Cooling,
        HMI: panelSizing.HMI,
        DoorMods: panelSizing.DoorMods,
        ModStd: panelSizing.ModSTD,
        Busbar: panelSizing.Busbar,
        FusedXL: panelSizing.FusedXL,
        S6XL: panelSizing.S6XL,
        SoftStart: panelSizing.SoftStart,
        Options: panelSizing.Options,
        Status: panelSizing.Status
      };
    });
  }, [generalProjectData, electricalOptionsData, waterTreatmentData, generalStationData, fertigationData, pumpsData, panelSizingData]);

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = combinedData;
    
    // Only show items with Status = "Done"
    filtered = filtered.filter(row => row.Status === 'Done');
    
    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(row => 
        row.JobNumber?.toLowerCase().includes(searchLower) ||
        row.ProjectName?.toLowerCase().includes(searchLower) ||
        row.ModelNumber?.toLowerCase().includes(searchLower)
      );
    }
    
    // Sort data
    const sorted = [...filtered].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;
      
      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
    
    return sorted;
  }, [combinedData, searchTerm, sortConfig]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageData = filteredAndSortedData.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortConfig.key, sortConfig.direction]);

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Handle field value change
  const handleFieldChange = (projectId, fieldName, value) => {
    setEditedValues(prev => ({
      ...prev,
      [projectId]: {
        ...(prev[projectId] || {}),
        [fieldName]: value
      }
    }));
  };

  // Get current value for a field (edited or original)
  const getFieldValue = (projectId, fieldName, originalValue) => {
    if (editedValues[projectId]?.[fieldName] !== undefined) {
      return editedValues[projectId][fieldName];
    }
    return originalValue || '';
  };

  // Helper function to check if value exists in dropdown
  const isValueInDropdown = (value, dropdownOptions) => {
    return dropdownOptions.some(
      (option) => option.DisplayText === value || option.displayName === value
    );
  };

  // Get Status dropdown options
  const statusOptions =
    options?.filter((option) => option.DropdownType === "Panel Review Status")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];

  // Record change to change log
  const recordChangeLog = async (projectId, jobNumber, jobName, fieldName, prevValue, newValue) => {
    if (!apiUrls.panelSizingChangeLog) return;
    
    try {
      const changeLogPayload = {
        projectID: projectId,
        currentUser: currentUser,
        userRole: primaryRole,
        jobNumber: jobNumber || '',
        jobName: jobName || '',
        field: fieldName,
        prevValue: prevValue === null || prevValue === undefined ? '' : String(prevValue),
        newValue: newValue === null || newValue === undefined ? '' : String(newValue),
        dateTime: new Date().toISOString()
      };
      
      await panelRequest(dispatch, 'POST', apiUrls.panelSizingChangeLog, changeLogPayload);
      console.log('Change log recorded successfully');
    } catch (error) {
      console.error('Error recording change log:', error);
      // Don't fail the save if change log fails
    }
  };

  // Handle field blur - save to database
  const handleFieldBlur = (projectId, fieldName, value) => serializePanelSave(
    dispatch, JSON.stringify([apiUrls.panelSizing, userDetails?.localAccountId, projectId]), async (assertCurrent) => {
    const fieldKey = `${projectId}_${fieldName}`;
    
    // Compare against the fresh record inside the queue, not stale render props.

    setSavingFields(prev => ({ ...prev, [fieldKey]: true }));

    try {
      assertCurrent();
      const panelSizingUrl = apiUrls.panelSizing;
      const isUpdatedApi = updatedApis.panelSizing;
      
      // Prepare the data payload - map ModStd to ModSTD for API (do this first)
      const apiFieldName = fieldName === 'ModStd' ? 'ModSTD' : fieldName;
      
      // Fetch existing record to verify if PATCH or POST is needed
      // This ensures only 1 record per projectID
      const checkUrl = `${panelSizingUrl}?projectId=${projectId}`;
      const existingRecord = await findPanelRecord(dispatch, checkUrl, projectId);
      assertCurrent();
      
      // Double-check against the fetched record to ensure value actually changed
      if (existingRecord) {
        const dbValue = existingRecord[apiFieldName];
        // Normalize both values for comparison (handle null, undefined, empty string)
        const normalizedDbValue = dbValue === null || dbValue === undefined ? '' : String(dbValue);
        const normalizedNewValue = value === null || value === undefined ? '' : String(value);
        
        if (normalizedDbValue === normalizedNewValue) {
          setEditedValues(prev => withoutEditedField(prev, projectId, fieldName));
          // No actual change in database, skip save
          console.log(`No change detected for ${fieldName}, skipping save`);
          setEditingFields(prev => {
            const updated = { ...prev };
            delete updated[fieldKey];
            return updated;
          });
          setSavingFields(prev => {
            const updated = { ...prev };
            delete updated[fieldKey];
            return updated;
          });
          return;
        }
      }
      
      if (existingRecord?.ID) {
        // PATCH existing record
        const payload = {
          ID: existingRecord.ID,
          ProjectID: projectId,
          [apiFieldName]: value
        };
        
        const url = isUpdatedApi 
          ? `${panelSizingUrl}?projectId=${projectId}`
          : `${panelSizingUrl}/${existingRecord.ID}`;
        
        await panelRequest(dispatch, 'PATCH', url, payload);
        
        if (showSaveNotificationMessage) {
          showSaveNotificationMessage('Panel sizing updated successfully', 'success');
        }
        
        // Record change to change log
        const jobInfo = generalProjectData.find((project) => idsMatch(getProjectId(project), projectId));
        await recordChangeLog(
          projectId,
          jobInfo?.JobNumber,
          jobInfo?.ProjectName,
          apiFieldName,
          existingRecord[apiFieldName],
          value
        );
      } else {
        // POST new record - use lowercase first letter
        const lowercaseField = apiFieldName.charAt(0).toLowerCase() + apiFieldName.slice(1);
        const payload = {
          projectID: projectId,
          [lowercaseField]: value
        };
        
        const url = isUpdatedApi
          ? `${panelSizingUrl}?projectID=${projectId}`
          : panelSizingUrl;
        
        await panelRequest(dispatch, 'POST', url, payload);
        
        if (showSaveNotificationMessage) {
          showSaveNotificationMessage('Panel sizing created successfully', 'success');
        }
        
        // Record change to change log
        const jobInfo = generalProjectData.find((project) => idsMatch(getProjectId(project), projectId));
        await recordChangeLog(
          projectId,
          jobInfo?.JobNumber,
          jobInfo?.ProjectName,
          apiFieldName,
          '',
          value
        );
      }

      // Trigger data refresh if callback provided - AWAIT to ensure data is fresh before clearing local state
      if (onDataUpdate) {
        await onDataUpdate();
      }

      // Clear edited value from state
      setEditedValues(prev => withoutEditedField(prev, projectId, fieldName));

      // Exit edit mode
      setEditingFields(prev => {
        const updated = { ...prev };
        delete updated[fieldKey];
        return updated;
      });
    } catch (error) {
      console.error(`Error saving ${fieldName}:`, error);
      if (showSaveNotificationMessage) {
        showSaveNotificationMessage(
          `Failed to save ${fieldName}: ${error.response?.data?.message || error.message}`,
          'danger'
        );
      }
    } finally {
      setSavingFields(prev => {
        const updated = { ...prev };
        delete updated[fieldKey];
        return updated;
      });
    }
  });

  // Render editable Status field with dropdown
  const renderEditableStatusField = (projectId, value) => {
    const fieldName = 'Status';
    const fieldKey = `${projectId}_${fieldName}`;
    const isSaving = savingFields[fieldKey];
    const isEditing = editingFields[fieldKey];
    const currentValue = getFieldValue(projectId, fieldName, value);

    // Handle double-click to enter edit mode
    const handleDoubleClick = () => {
      setEditingFields(prev => ({ ...prev, [fieldKey]: true }));
    };

    // Handle dropdown change
    const handleDropdownChange = (e) => {
      const newValue = e.target.value;
      handleFieldChange(projectId, fieldName, newValue);
      // Auto-save on dropdown change
      handleFieldBlur(projectId, fieldName, newValue);
    };

    if (isEditing) {
      return (
        <Form.Select
          size="sm"
          value={currentValue}
          onChange={handleDropdownChange}
          disabled={isSaving}
          autoFocus
          style={{
            minWidth: '120px',
            backgroundColor: isSaving ? '#f8f9fa' : 'white',
            fontSize: '0.875rem'
          }}
        >
          <option value="">Select...</option>
          {statusOptions.map((option) => (
            <option key={option.ID} value={option.DisplayText || option.displayName}>
              {option.DisplayText || option.displayName}
            </option>
          ))}
        </Form.Select>
      );
    }

    // Display value with validation indicator
    const isValid = !currentValue || isValueInDropdown(currentValue, statusOptions);
    
    return (
      <div
        onDoubleClick={handleDoubleClick}
        style={{
          cursor: 'pointer',
          padding: '4px 8px',
          minHeight: '24px',
          borderRadius: '4px',
          transition: 'background-color 0.2s',
          color: isValid ? 'inherit' : '#dc3545',
          fontWeight: isValid ? 'normal' : 'bold'
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        title={isValid ? 'Double-click to edit' : `Invalid value: ${currentValue}`}
      >
        {currentValue || '-'}
      </div>
    );
  };

  // Render editable field
  const renderEditableField = (projectId, fieldName, value) => {
    const fieldKey = `${projectId}_${fieldName}`;
    const isSaving = savingFields[fieldKey];
    const isEditing = editingFields[fieldKey];
    const currentValue = getFieldValue(projectId, fieldName, value);

    // Handle double-click to enter edit mode
    const handleDoubleClick = () => {
      setEditingFields(prev => ({ ...prev, [fieldKey]: true }));
    };

    // Handle key press (Enter to save, Escape to cancel)
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.target.blur(); // Trigger save
      } else if (e.key === 'Escape') {
        // Cancel edit - reset to original value
        setEditedValues(prev => {
          const updated = { ...prev };
          if (updated[projectId]) {
            delete updated[projectId][fieldName];
            if (Object.keys(updated[projectId]).length === 0) {
              delete updated[projectId];
            }
          }
          return updated;
        });
        setEditingFields(prev => {
          const updated = { ...prev };
          delete updated[fieldKey];
          return updated;
        });
      }
    };

    if (isEditing) {
      return (
        <Form.Control
          type="text"
          size="sm"
          value={currentValue}
          onChange={(e) => handleFieldChange(projectId, fieldName, e.target.value)}
          onBlur={(e) => handleFieldBlur(projectId, fieldName, e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isSaving}
          autoFocus
          style={{
            minWidth: '80px',
            backgroundColor: isSaving ? '#f8f9fa' : 'white',
            fontSize: '0.875rem'
          }}
        />
      );
    }

    return (
      <div
        onDoubleClick={handleDoubleClick}
        style={{
          cursor: 'pointer',
          padding: '4px 8px',
          minHeight: '24px',
          borderRadius: '4px',
          transition: 'background-color 0.2s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        title="Double-click to edit"
      >
        {currentValue || '-'}
      </div>
    );
  };

  // Check if job number is valid (exists and starts with 20XX year format)
  const isValidJobNumber = (jobNumber) => {
    if (!jobNumber || jobNumber.length < 4) return false;
    const year = jobNumber.substring(0, 4);
    // Check if starts with "20" and the next two characters are digits
    return year.startsWith('20') && !isNaN(year);
  };

  // Generate file path based on job number
  const generateFilePath = (jobNumber) => {
    if (!isValidJobNumber(jobNumber)) return null;
    
    // Extract first 4 characters (year) from job number
    const year = jobNumber.substring(0, 4);
    
    // Construct the full path
    return `\\\\h2ofs01\\Engineering\\ELECTRICAL\\WT_ACADE\\PROJECTS\\JOBS\\${year}\\${jobNumber}`;
  };

  // Handle file path click - try to open file explorer, fall back to clipboard
  const handleFilePathClick = (e, jobNumber) => {
    e.stopPropagation();
    const path = generateFilePath(jobNumber);
    if (path) {
      // Try to open in file explorer using file:// protocol
      // Note: This may be blocked by browser security policies
      const fileUrl = `file://` + path.replace(/\\\\/g, '/');
      
      // Attempt to open in new window/tab
      const opened = window.open(fileUrl, '_blank');
      
      // If window.open is blocked or fails, copy to clipboard as fallback
      if (!opened || opened.closed || typeof opened.closed === 'undefined') {
        navigator.clipboard.writeText(path).then(() => {
          console.log('File path copied to clipboard:', path);
          // Show visual feedback
          setCopiedJobId(jobNumber);
          setTimeout(() => setCopiedJobId(null), 2000);
        }).catch(err => {
          console.error('Failed to copy path:', err);
        });
      }
    }
  };

  // Generate folder path based on job number
  const generateFolderPath = (jobNumber) => {
    if (!isValidJobNumber(jobNumber)) return null;
    
    // Extract first 4 characters (year) from job number
    const year = jobNumber.substring(0, 4);
    // Get last 2 digits of year
    const yearShort = year.substring(2, 4);
    
    // Construct the full path
    return `\\\\h2ofs01\\jobs\\${yearShort}_Jobs\\${jobNumber}\\ELECTRICAL\\SCHEMATICS-BOM-IO\\PANEL`;
  };

  // Handle folder path click - try to open file explorer, fall back to clipboard
  const handleFolderPathClick = (e, jobNumber) => {
    e.stopPropagation();
    const path = generateFolderPath(jobNumber);
    if (path) {
      // Try to open in file explorer using file:// protocol
      // Note: This may be blocked by browser security policies
      const fileUrl = `file://` + path.replace(/\\\\/g, '/');
      
      // Attempt to open in new window/tab
      const opened = window.open(fileUrl, '_blank');
      
      // If window.open is blocked or fails, copy to clipboard as fallback
      if (!opened || opened.closed || typeof opened.closed === 'undefined') {
        navigator.clipboard.writeText(path).then(() => {
          console.log('Folder path copied to clipboard:', path);
          // Show visual feedback
          setCopiedFolderJobId(jobNumber);
          setTimeout(() => setCopiedFolderJobId(null), 2000);
        }).catch(err => {
          console.error('Failed to copy path:', err);
        });
      }
    }
  };

  const renderSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <i className="bi bi-chevron-expand ms-1" style={{ fontSize: '0.8rem', color: '#999' }}></i>;
    }
    return sortConfig.direction === 'ascending' ? 
      <i className="bi bi-chevron-up ms-1" style={{ fontSize: '0.8rem' }}></i> : 
      <i className="bi bi-chevron-down ms-1" style={{ fontSize: '0.8rem' }}></i>;
  };

  if (loading && combinedData.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error Loading Data</Alert.Heading>
        <p>{error}</p>
      </Alert>
    );
  }

  return (
    <div className="log-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Complete — Electrical Job Log</h3>
        <div className="d-flex gap-2 align-items-center">
          <span className="text-muted">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredAndSortedData.length)} of {filteredAndSortedData.length} jobs
          </span>
          <Form.Control
            type="text"
            placeholder="Search by Job, Description, or Model..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '300px' }}
          />
        </div>
      </div>

      <div className="table-responsive">
        <Table striped bordered hover size="sm" className="log-table">
          <thead>
            <tr>
              <th>L</th>
              <th>M</th>
              <th onClick={() => handleSort('Status')} style={{ cursor: 'pointer' }}>
                STATUS {renderSortIcon('Status')}
              </th>
              <th onClick={() => handleSort('JobNumber')} style={{ cursor: 'pointer' }}>
                JOB {renderSortIcon('JobNumber')}
              </th>
              <th onClick={() => handleSort('ProjectName')} style={{ cursor: 'pointer' }}>
                DESCRIPTION {renderSortIcon('ProjectName')}
              </th>
              <th onClick={() => handleSort('ModelNumber')} style={{ cursor: 'pointer' }}>
                MODEL {renderSortIcon('ModelNumber')}
              </th>
              <th onClick={() => handleSort('ElectricalEnclosureActualSize')} style={{ cursor: 'pointer' }}>
                SIZE {renderSortIcon('ElectricalEnclosureActualSize')}
              </th>
              <th>ORIENTATION</th>
              <th>DRIVES</th>
              <th>COOLING</th>
              <th>HMI</th>
              <th onClick={() => handleSort('Sunshade')} style={{ cursor: 'pointer' }}>
                SUNSHADE {renderSortIcon('Sunshade')}
              </th>
              <th>DOOR MODS</th>
              <th onClick={() => handleSort('DeadFront')} style={{ cursor: 'pointer' }}>
                DEAD FRONT {renderSortIcon('DeadFront')}
              </th>
              <th onClick={() => handleSort('ElectricalOptions')} style={{ cursor: 'pointer' }}>
                JBOX {renderSortIcon('ElectricalOptions')}
              </th>
              <th onClick={() => handleSort('HasUV')} style={{ cursor: 'pointer' }}>
                UV PANEL {renderSortIcon('HasUV')}
              </th>
              <th onClick={() => handleSort('PrimeSystem')} style={{ cursor: 'pointer' }}>
                VAC PRIME {renderSortIcon('PrimeSystem')}
              </th>
              <th onClick={() => handleSort('WatertronicsFertigation')} style={{ cursor: 'pointer' }}>
                FERTIGATION {renderSortIcon('WatertronicsFertigation')}
              </th>
              <th onClick={() => handleSort('Zones')} style={{ cursor: 'pointer' }}>
                ZONE {renderSortIcon('Zones')}
              </th>
              <th>MOD STD</th>
              <th>BUSBAR</th>
              <th>FUSED XL</th>
              <th>S6 XL</th>
              <th>SOFT START</th>
              <th onClick={() => handleSort('IndividualMotorDisconnects')} style={{ cursor: 'pointer' }}>
                MOTOR DISCONNECTS {renderSortIcon('IndividualMotorDisconnects')}
              </th>
              <th>TR OR WF</th>
              <th>OPTIONS</th>
              <th>JOB - DESCRIPTION (MODEL)</th>
            </tr>
          </thead>
          <tbody>
            {currentPageData.length === 0 ? (
              <tr>
                <td colSpan="28" className="text-center text-muted py-4">
                  {searchTerm ? 'No jobs match your search criteria' : 'No jobs to display'}
                </td>
              </tr>
            ) : (
              currentPageData.map((row) => (
                <tr key={row.ID}>
                  <td className="text-center">
                    {isValidJobNumber(row.JobNumber) ? (
                      <div
                        style={{ cursor: 'pointer', position: 'relative' }}
                        onClick={(e) => handleFilePathClick(e, row.JobNumber)}
                        title={generateFilePath(row.JobNumber)}
                      >
                        <i 
                          className="bi bi-file-earmark" 
                          style={{ 
                            fontSize: '1.2rem', 
                            color: copiedJobId === row.JobNumber ? '#198754' : '#0d6efd',
                            transition: 'color 0.2s'
                          }}
                          onMouseEnter={(e) => copiedJobId !== row.JobNumber && (e.currentTarget.style.color = '#0a58ca')}
                          onMouseLeave={(e) => copiedJobId !== row.JobNumber && (e.currentTarget.style.color = '#0d6efd')}
                        ></i>
                        {copiedJobId === row.JobNumber && (
                          <small 
                            style={{ 
                              position: 'absolute', 
                              top: '100%', 
                              left: '50%', 
                              transform: 'translateX(-50%)',
                              whiteSpace: 'nowrap',
                              color: '#198754',
                              fontSize: '0.7rem',
                              fontWeight: 'bold'
                            }}
                          >
                            Copied!
                          </small>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: '#999' }}>-</span>
                    )}
                  </td>
                  <td className="text-center">
                    {isValidJobNumber(row.JobNumber) ? (
                      <div
                        style={{ cursor: 'pointer', position: 'relative' }}
                        onClick={(e) => handleFolderPathClick(e, row.JobNumber)}
                        title={generateFolderPath(row.JobNumber)}
                      >
                        <i 
                          className="bi bi-folder" 
                          style={{ 
                            fontSize: '1.2rem', 
                            color: copiedFolderJobId === row.JobNumber ? '#198754' : '#ffc107',
                            transition: 'color 0.2s'
                          }}
                          onMouseEnter={(e) => copiedFolderJobId !== row.JobNumber && (e.currentTarget.style.color = '#ffca2c')}
                          onMouseLeave={(e) => copiedFolderJobId !== row.JobNumber && (e.currentTarget.style.color = '#ffc107')}
                        ></i>
                        {copiedFolderJobId === row.JobNumber && (
                          <small 
                            style={{ 
                              position: 'absolute', 
                              top: '100%', 
                              left: '50%', 
                              transform: 'translateX(-50%)',
                              whiteSpace: 'nowrap',
                              color: '#198754',
                              fontSize: '0.7rem',
                              fontWeight: 'bold'
                            }}
                          >
                            Copied!
                          </small>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: '#999' }}>-</span>
                    )}
                  </td>
                  <td>{renderEditableStatusField(row.ID, row.Status)}</td>
                  <td>{row.JobNumber || '-'}</td>
                  <td>{row.ProjectName || '-'}</td>
                  <td>{row.ModelNumber || '-'}</td>
                  <td>{row.ElectricalEnclosureActualSize || '-'}</td>
                  <td>{renderEditableField(row.ID, 'Orientation', row.Orientation)}</td>
                  <td>{renderEditableField(row.ID, 'Drives', row.Drives)}</td>
                  <td>{renderEditableField(row.ID, 'Cooling', row.Cooling)}</td>
                  <td>{renderEditableField(row.ID, 'HMI', row.HMI)}</td>
                  <td>{row.Sunshade || '-'}</td>
                  <td>{renderEditableField(row.ID, 'DoorMods', row.DoorMods)}</td>
                  <td>{row.DeadFront || '-'}</td>
                  <td>{row.ElectricalOptions || '-'}</td>
                  <td>{row.HasUV ? 'Yes' : 'No'}</td>
                  <td>{row.PrimeSystem || '-'}</td>
                  <td>{row.WatertronicsFertigation || '-'}</td>
                  <td>{row.Zones || '-'}</td>
                  <td>{renderEditableField(row.ID, 'ModStd', row.ModStd)}</td>
                  <td>{renderEditableField(row.ID, 'Busbar', row.Busbar)}</td>
                  <td>{renderEditableField(row.ID, 'FusedXL', row.FusedXL)}</td>
                  <td>{renderEditableField(row.ID, 'S6XL', row.S6XL)}</td>
                  <td>{renderEditableField(row.ID, 'SoftStart', row.SoftStart)}</td>
                  <td>{row.IndividualMotorDisconnects || '-'}</td>
                  <td>
                    {row.HasTransfer && row.HasWaterFeature ? 'Both' : 
                     row.HasTransfer ? 'Transfer' : 
                     row.HasWaterFeature ? 'Water Feature' : '-'}
                  </td>
                  <td style={{ minWidth: '200px' }}>{renderEditableField(row.ID, 'Options', row.Options)}</td>
                  <td>
                    {row.JobNumber && row.ProjectName && row.ModelNumber 
                      ? `${row.JobNumber} - ${row.ProjectName} (${row.ModelNumber})`
                      : '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center align-items-center mt-3 gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            First
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          
          <span className="mx-3">
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </span>
          
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
          >
            Last
          </Button>
        </div>
      )}

      <style jsx>{`
        .log-container {
          padding: 20px;
          background-color: #fff;
          border-radius: 0.375rem;
          box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
        }

        .log-table {
          font-size: 0.875rem;
          white-space: nowrap;
        }

        .log-table th {
          background-color: #f8f9fa;
          font-weight: 600;
          vertical-align: middle;
          padding: 8px;
          border: 1px solid #dee2e6;
        }

        .log-table td {
          vertical-align: middle;
          padding: 6px 8px;
          border: 1px solid #dee2e6;
        }

        .log-table tbody tr:hover {
          background-color: #f1f3f5;
        }

        .table-responsive {
          overflow-x: auto;
          max-width: 100%;
        }
      `}</style>
    </div>
  );
}

export default PanelSizingLog;
